import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { SendHorizonal } from 'lucide-react';
import { z } from 'zod';

import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { ShareTaskViaEmail } from '@/task-manager/api/mutations/share-task-via-email';
import type { TaskDetails } from '@/task-manager/entities/task-details';

export type ShareTaskDialogProps = {
  task: TaskDetails;
  open: boolean;
  onClose: () => void;
};

export const ShareTaskFormSchema = z.object({ email: z.string().email() });
export type ShareTaskForm = z.infer<typeof ShareTaskFormSchema>;

export function ShareTaskDialog({ task, open, onClose }: ShareTaskDialogProps) {
  const shareTaskViaEmail = ShareTaskViaEmail.useMutation();
  const form = useForm<ShareTaskForm>({
    resolver: zodResolver(ShareTaskFormSchema),
  });
  const { isValid } = form.formState;

  async function handleSubmit(data: ShareTaskForm) {
    await toast.promise(
      shareTaskViaEmail.mutateAsync({
        id: task.id,
        emails: [data.email],
      }),
      {
        pending: 'Sending email...',
        success: 'Email successfully sent!',
        error: 'An error occurred, please try again.',
      },
    );

    form.reset();
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} className="mt-24 w-96">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} autoComplete="off">
          <DialogTitle>Send Task via Email</DialogTitle>
          <DialogDescription>
            The recipient will receive the details of the task via email.
          </DialogDescription>
          <DialogBody>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      autoFocus
                      placeholder="jane.doe@accure.net"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </DialogBody>
          <DialogActions>
            <Button
              type="submit"
              disabled={!isValid || shareTaskViaEmail.isLoading}
            >
              <SendHorizonal />
              Send
            </Button>
          </DialogActions>
        </form>
      </Form>
    </Dialog>
  );
}
